import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { SeoHead, organizationLd, webSiteLd, datasetLd } from '../seo';

interface LeaderboardEntry {
  city: string;
  slug: string;
  avg_monthly: number | null;
  count: number;
}

export default function IndiaParkingReport() {
  const [cheapest, setCheapest] = useState<LeaderboardEntry[]>([]);
  const [expensive, setExpensive] = useState<LeaderboardEntry[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string>('');

  useEffect(() => {
    Promise.all([
      fetch('/api/stats/leaderboard?sort=cheapest&min_count=1&limit=12').then((r) => r.json()),
      fetch('/api/stats/leaderboard?sort=expensive&min_count=1&limit=12').then((r) => r.json()),
    ])
      .then(([c, e]) => {
        if (Array.isArray(c?.data?.results)) setCheapest(c.data.results);
        if (Array.isArray(e?.data?.results)) setExpensive(e.data.results);
      })
      .catch(() => undefined);
    setUpdatedAt(new Date().toISOString());
  }, []);

  return (
    <>
      <SeoHead
        title="India Parking Report — Live Crowdsourced Data | Parksy"
        description="The India Parking Report is a monthly snapshot of parking prices across 12 metros, sourced from crowdsourced data on parksy.rent."
        canonical="https://parksy.rent/india-parking-report"
        ogImage="https://parksy.rent/og/india-parking-report.png"
        schemas={[
          organizationLd(),
          webSiteLd(),
          datasetLd({
            name: 'India Parking Report',
            description: 'Monthly snapshot of parking prices across Indian metros.',
            url: 'https://parksy.rent/india-parking-report',
            citySlug: 'india',
            updatedAt,
          }),
        ]}
      />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <header className="mb-8">
          <div className="text-xs uppercase tracking-wide text-parksy-700 font-semibold mb-2">
            Monthly data report
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">The India Parking Report</h1>
          <p className="text-lg text-gray-700">
            A monthly snapshot of parking prices across {cheapest.length || expensive.length || 12} Indian
            metros, sourced from crowdsourced data on parksy.rent. Updated on the 1st of every
            month.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Last updated: {updatedAt ? new Date(updatedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'Loading…'}
          </p>
        </header>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">The top-line number</h2>
          <div className="bg-white border rounded-xl p-6">
            {expensive[0] ? (
              <>
                <p className="text-sm text-gray-500 mb-2">Most expensive metro for monthly car parking</p>
                <p className="text-4xl font-bold text-gray-900 mb-2">
                  ₹{Math.round(expensive[0].avg_monthly ?? 0).toLocaleString('en-IN')}/month
                </p>
                <p className="text-sm text-gray-700">
                  {expensive[0].city}, based on{' '}
                  <strong>{expensive[0].count} active listings</strong> within 25 km of the city
                  centre.
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500">Data populates as listings grow.</p>
            )}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cheapest metros for monthly parking</h2>
          <div className="bg-white border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2">Rank</th>
                  <th className="text-left px-4 py-2">City</th>
                  <th className="text-right px-4 py-2">Listings</th>
                  <th className="text-right px-4 py-2">Avg ₹/month</th>
                </tr>
              </thead>
              <tbody>
                {cheapest.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-gray-500 py-6">
                      Leaderboard populates as listings grow.
                    </td>
                  </tr>
                ) : (
                  cheapest.map((row, idx) => (
                    <tr key={row.slug} className="border-t">
                      <td className="px-4 py-2 text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-2 font-medium text-gray-900">
                        <Link to={`/${row.slug}`} className="hover:text-parksy-600">
                          {row.city}
                        </Link>
                      </td>
                      <td className="px-4 py-2 text-right text-gray-600">{row.count}</td>
                      <td className="px-4 py-2 text-right text-gray-600">
                        ₹{Math.round(row.avg_monthly ?? 0).toLocaleString('en-IN')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Most expensive metros</h2>
          <div className="bg-white border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2">Rank</th>
                  <th className="text-left px-4 py-2">City</th>
                  <th className="text-right px-4 py-2">Listings</th>
                  <th className="text-right px-4 py-2">Avg ₹/month</th>
                </tr>
              </thead>
              <tbody>
                {expensive.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-gray-500 py-6">
                      Leaderboard populates as listings grow.
                    </td>
                  </tr>
                ) : (
                  expensive.map((row, idx) => (
                    <tr key={row.slug} className="border-t">
                      <td className="px-4 py-2 text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-2 font-medium text-gray-900">
                        <Link to={`/${row.slug}`} className="hover:text-parksy-600">
                          {row.city}
                        </Link>
                      </td>
                      <td className="px-4 py-2 text-right text-gray-600">{row.count}</td>
                      <td className="px-4 py-2 text-right text-gray-600">
                        ₹{Math.round(row.avg_monthly ?? 0).toLocaleString('en-IN')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">How the data is collected</h2>
          <p className="text-gray-700 text-sm mb-3">
            Anyone can drop a "price pin" on parksy.rent — what they currently pay, anonymously.
            We aggregate those pins by city, filter outliers, and surface medians, p25, p75, and
            averages. No personal data is exposed. Methodology details: see the parksy price
            transparency methodology.
          </p>
          <p className="text-gray-700 text-sm mb-3">
            <strong>Caveats:</strong> city-level data is a 25 km radius around the city centre.
            Premium areas within a city (Bandra vs Andheri in Mumbai) trend well above the
            city-level median; see the locality pages for finer breakdowns.
          </p>
          <Link
            to="/pin-your-rent"
            className="text-parksy-700 hover:underline text-sm"
          >
            Pin what you pay →
          </Link>
        </section>
      </div>
    </>
  );
}
